import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from './ui/command';
import { Textarea } from './ui/textarea';
import { 
  Send, UserPlus, X, Check, AtSign, Loader2, Paperclip, FileText, 
  Image, Download, Trash2, Mic, Square, Play, Pause, MoreVertical,
  Volume2, Clock, MessageSquare
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { toast } from 'sonner';

const ApplicationChat = ({ open, onOpenChange, application }) => {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [addingUser, setAddingUser] = useState(false);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [selectedMentions, setSelectedMentions] = useState([]);
  const [pendingAttachments, setPendingAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(null); // message id being played
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [messageToDelete, setMessageToDelete] = useState(null);
  
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const recordingTimerRef = useRef(null);
  const audioElementsRef = useRef({});

  useEffect(() => {
    if (open && application?.id) {
      fetchChat();
      fetchUsers();
      markAsRead();
    }
    return () => {
      // Cleanup recording timer
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [open, application?.id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    if (!open || !application?.id) return;
    const interval = setInterval(() => fetchChat(true), 5000);
    return () => clearInterval(interval);
  }, [open, application?.id]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 150) + 'px';
    }
  }, [newMessage]);

  const fetchChat = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const response = await api().get(`/applications/${application.id}/chat`);
      setMessages(response.data.messages || []);
      setParticipants(response.data.participants || []);
    } catch (error) {
      if (!silent) toast.error('Failed to load chat');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api().get('/users');
      setAllUsers(response.data || []);
    } catch (error) {
      console.error('Failed to load users');
    }
  };

  const markAsRead = async () => {
    try {
      await api().post(`/applications/${application.id}/chat/mark-read`);
    } catch (error) {
      // Silent fail
    }
  };

  const sendMessage = async (e) => {
    e?.preventDefault();
    if (!newMessage.trim() && pendingAttachments.length === 0 && !audioBlob) return;
    
    setSending(true);
    try {
      // Upload voice note first if exists
      let attachmentIds = pendingAttachments.map(a => a.id);
      
      if (audioBlob) {
        const formData = new FormData();
        formData.append('file', audioBlob, 'voice_note.webm');
        const voiceResponse = await api().post(
          `/applications/${application.id}/chat/voice-note?duration=${recordingDuration}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        attachmentIds.push(voiceResponse.data.id);
      }
      
      const mentionedIds = selectedMentions.map(m => m.id);
      const content = newMessage.trim() || (attachmentIds.length > 0 
        ? (audioBlob ? '🎤 Voice message' : `📎 Shared ${attachmentIds.length} file(s)`) 
        : '');
      
      await api().post(`/applications/${application.id}/chat`, {
        content,
        mentioned_user_ids: mentionedIds,
        attachment_ids: attachmentIds
      });
      
      setNewMessage('');
      setSelectedMentions([]);
      setPendingAttachments([]);
      setAudioBlob(null);
      setAudioUrl(null);
      setRecordingDuration(0);
      fetchChat(true);
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const confirmDeleteMessage = (message) => {
    setMessageToDelete(message);
    setDeleteDialogOpen(true);
  };

  const deleteMessage = async () => {
    if (!messageToDelete) return;
    try {
      await api().delete(`/applications/${application.id}/chat/${messageToDelete.id}`);
      setMessages(messages.filter(m => m.id !== messageToDelete.id));
      toast.success('Message deleted');
    } catch (error) {
      toast.error('Failed to delete message');
    } finally {
      setDeleteDialogOpen(false);
      setMessageToDelete(null);
    }
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          audioChunksRef.current.push(e.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      
      // Start timer
      recordingTimerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Microphone access error:', error);
      toast.error('Could not access microphone. Please allow microphone permission.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    }
  };

  const cancelRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingDuration(0);
    if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
    }
  };

  const playVoiceNote = async (attachment) => {
    try {
      if (isPlaying === attachment.id) {
        // Stop current playback
        if (audioElementsRef.current[attachment.id]) {
          audioElementsRef.current[attachment.id].pause();
          audioElementsRef.current[attachment.id].currentTime = 0;
        }
        setIsPlaying(null);
        return;
      }
      
      // Stop any other playing audio
      Object.values(audioElementsRef.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
        }
      });
      
      if (!audioElementsRef.current[attachment.id]) {
        const response = await api().get(`/chat/attachments/${attachment.id}/download`, {
          responseType: 'blob'
        });
        const url = URL.createObjectURL(new Blob([response.data]));
        const audio = new Audio(url);
        audio.onended = () => setIsPlaying(null);
        audioElementsRef.current[attachment.id] = audio;
      }
      
      audioElementsRef.current[attachment.id].play();
      setIsPlaying(attachment.id);
    } catch (error) {
      toast.error('Failed to play voice note');
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleFileSelect = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const allowedTypes = ['pdf', 'docx', 'doc', 'jpeg', 'jpg', 'png'];
    const maxSize = 10 * 1024 * 1024;
    
    setUploading(true);
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const ext = file.name.split('.').pop().toLowerCase();
      
      if (!allowedTypes.includes(ext)) {
        toast.error(`${file.name}: Invalid file type`);
        continue;
      }
      
      if (file.size > maxSize) {
        toast.error(`${file.name}: File too large (max 10MB)`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api().post(
          `/applications/${application.id}/chat/upload`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setPendingAttachments(prev => [...prev, response.data]);
        toast.success(`${file.name} uploaded`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingAttachment = (attachmentId) => {
    setPendingAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const downloadAttachment = async (attachment) => {
    try {
      const response = await api().get(`/chat/attachments/${attachment.id}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', attachment.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download file');
    }
  };

  const addParticipant = async (userId) => {
    setAddingUser(true);
    try {
      await api().post(`/applications/${application.id}/chat/participants`, { user_ids: [userId] });
      toast.success('User added to chat');
      fetchChat(true);
    } catch (error) {
      toast.error('Failed to add user');
    } finally {
      setAddingUser(false);
    }
  };

  const handleMention = (mentionUser) => {
    if (!selectedMentions.find(m => m.id === mentionUser.id)) {
      setSelectedMentions([...selectedMentions, mentionUser]);
      setNewMessage(prev => prev + `@${mentionUser.name} `);
    }
    setMentionOpen(false);
    textareaRef.current?.focus();
  };

  const removeMention = (userId) => {
    setSelectedMentions(selectedMentions.filter(m => m.id !== userId));
  };

  const getInitials = (name) => {
    return name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || '??';
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' }) + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getFileIcon = (fileType) => {
    const imageTypes = ['JPEG', 'JPG', 'PNG'];
    if (imageTypes.includes(fileType?.toUpperCase())) {
      return <Image className="w-4 h-4" />;
    }
    return <FileText className="w-4 h-4" />;
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const nonParticipantUsers = allUsers.filter(u => !participants.find(p => p.id === u.id));

  // Handle keyboard shortcut (Ctrl/Cmd + Enter to send)
  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      sendMessage(e);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="px-5 py-4 border-b bg-gradient-to-r from-primary/5 to-transparent">
          <DialogTitle className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <span className="font-semibold">{application?.student_name}</span>
              <Badge variant="outline" className="ml-2 text-xs">{application?.current_desk}</Badge>
            </div>
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2 flex-wrap mt-2">
            <span className="text-xs text-muted-foreground">Participants:</span>
            {participants.map(p => (
              <Badge key={p.id} variant="secondary" className="text-xs">{p.name}</Badge>
            ))}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" data-testid="add-participant-btn">
                  <UserPlus className="w-3 h-3 mr-1" />Add
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-64 p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search users..." />
                  <CommandList>
                    <CommandEmpty>No users found</CommandEmpty>
                    <CommandGroup>
                      {nonParticipantUsers.map(u => (
                        <CommandItem key={u.id} onSelect={() => addParticipant(u.id)} className="cursor-pointer">
                          <Avatar className="w-6 h-6 mr-2"><AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback></Avatar>
                          <span>{u.name}</span>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
          </DialogDescription>
        </DialogHeader>

        {/* Messages Area */}
        <ScrollArea className="flex-1 px-5 py-4">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary/50" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-muted flex items-center justify-center">
                <MessageSquare className="w-8 h-8 text-muted-foreground" />
              </div>
              <p className="font-medium text-muted-foreground">No messages yet</p>
              <p className="text-sm text-muted-foreground/70 mt-1">Start the conversation</p>
            </div>
          ) : (
            <div className="space-y-4">
              {messages.map((msg) => {
                const isOwn = msg.sender_id === user?.id;
                const hasAttachments = msg.attachments && msg.attachments.length > 0;
                
                return (
                  <div 
                    key={msg.id} 
                    className={`flex gap-3 ${isOwn ? 'flex-row-reverse' : ''} group`}
                    data-testid={`chat-message-${msg.id}`}
                  >
                    <Avatar className="w-9 h-9 flex-shrink-0 ring-2 ring-background">
                      <AvatarFallback className={`text-xs font-medium ${isOwn ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                        {getInitials(msg.sender_name)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'} max-w-[75%]`}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs font-medium">{msg.sender_name}</span>
                        <span className="text-xs text-muted-foreground">{formatTime(msg.created_at)}</span>
                        
                        {/* Delete Menu - only for own messages */}
                        {isOwn && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-muted" data-testid={`message-menu-${msg.id}`}>
                                <MoreVertical className="w-3.5 h-3.5 text-muted-foreground" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-36">
                              <DropdownMenuItem 
                                onClick={() => confirmDeleteMessage(msg)}
                                className="text-destructive focus:text-destructive cursor-pointer"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </div>
                      
                      <div className={`px-4 py-2.5 rounded-2xl ${
                        isOwn 
                          ? 'bg-primary text-primary-foreground rounded-tr-md' 
                          : 'bg-muted rounded-tl-md'
                      }`}>
                        {msg.content && (
                          <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{msg.content}</p>
                        )}
                        
                        {hasAttachments && (
                          <div className={`${msg.content ? 'mt-2 pt-2 border-t' : ''} ${
                            isOwn ? 'border-primary-foreground/20' : 'border-border'
                          } space-y-2`}>
                            {msg.attachments.map(att => (
                              att.is_voice_note || att.file_type === 'VOICE' ? (
                                // Voice Note Player
                                <div 
                                  key={att.id}
                                  className={`flex items-center gap-3 p-2.5 rounded-lg ${
                                    isOwn ? 'bg-primary-foreground/10' : 'bg-background/60'
                                  }`}
                                >
                                  <button
                                    onClick={() => playVoiceNote(att)}
                                    className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
                                      isOwn 
                                        ? 'bg-primary-foreground/20 hover:bg-primary-foreground/30' 
                                        : 'bg-primary/10 hover:bg-primary/20'
                                    }`}
                                    data-testid={`play-voice-${att.id}`}
                                  >
                                    {isPlaying === att.id ? (
                                      <Pause className={`w-5 h-5 ${isOwn ? 'text-primary-foreground' : 'text-primary'}`} />
                                    ) : (
                                      <Play className={`w-5 h-5 ml-0.5 ${isOwn ? 'text-primary-foreground' : 'text-primary'}`} />
                                    )}
                                  </button>
                                  <div className="flex-1">
                                    <div className={`h-1.5 rounded-full ${isOwn ? 'bg-primary-foreground/30' : 'bg-primary/20'}`}>
                                      <div className={`h-full rounded-full ${isOwn ? 'bg-primary-foreground/70' : 'bg-primary/60'}`} style={{ width: '0%' }} />
                                    </div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <Volume2 className={`w-3 h-3 ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`} />
                                      <span className={`text-xs ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                        {formatDuration(att.duration || 0)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                // Regular Attachment
                                <div 
                                  key={att.id}
                                  onClick={() => downloadAttachment(att)}
                                  className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                                    isOwn ? 'hover:bg-primary-foreground/10' : 'hover:bg-background/80'
                                  }`}
                                  data-testid={`attachment-${att.id}`}
                                >
                                  {getFileIcon(att.file_type)}
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium truncate">{att.filename}</p>
                                    <p className={`text-xs ${isOwn ? 'text-primary-foreground/70' : 'text-muted-foreground'}`}>
                                      {att.file_type} • {formatFileSize(att.file_size)}
                                    </p>
                                  </div>
                                  <Download className="w-4 h-4 flex-shrink-0 opacity-60" />
                                </div>
                              )
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>
          )}
        </ScrollArea>

        {/* Mentions Bar */}
        {selectedMentions.length > 0 && (
          <div className="px-5 py-2 border-t bg-blue-50/50 flex items-center gap-2 flex-wrap">
            <AtSign className="w-3.5 h-3.5 text-blue-500" />
            <span className="text-xs text-blue-600 font-medium">Mentioning:</span>
            {selectedMentions.map(m => (
              <Badge key={m.id} variant="secondary" className="text-xs gap-1 bg-blue-100 text-blue-700 hover:bg-blue-200">
                @{m.name}
                <X className="w-3 h-3 cursor-pointer" onClick={() => removeMention(m.id)} />
              </Badge>
            ))}
          </div>
        )}

        {/* Pending Attachments */}
        {pendingAttachments.length > 0 && (
          <div className="px-5 py-2 border-t bg-muted/30 flex items-center gap-2 flex-wrap">
            <Paperclip className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs text-muted-foreground font-medium">Attachments:</span>
            {pendingAttachments.map(att => (
              <div key={att.id} className="flex items-center gap-1.5 px-2 py-1 bg-background rounded-md border text-xs">
                {getFileIcon(att.file_type)}
                <span className="truncate max-w-[120px]">{att.filename}</span>
                <X className="w-3 h-3 cursor-pointer text-muted-foreground hover:text-destructive" onClick={() => removePendingAttachment(att.id)} />
              </div>
            ))}
          </div>
        )}

        {/* Voice Recording Preview */}
        {audioBlob && !isRecording && (
          <div className="px-5 py-3 border-t bg-emerald-50/50 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
              <Mic className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-emerald-700">Voice Note Ready</p>
              <p className="text-xs text-emerald-600">{formatDuration(recordingDuration)}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={cancelRecording}
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <X className="w-4 h-4 mr-1" />
              Discard
            </Button>
          </div>
        )}

        {/* Recording Indicator */}
        {isRecording && (
          <div className="px-5 py-3 border-t bg-red-50 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center animate-pulse">
              <div className="w-3 h-3 rounded-full bg-red-500" />
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-700">Recording...</p>
              <p className="text-xs text-red-600">{formatDuration(recordingDuration)}</p>
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={cancelRecording}
              className="text-muted-foreground hover:text-destructive"
            >
              <X className="w-4 h-4" />
            </Button>
            <Button 
              onClick={stopRecording}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Square className="w-4 h-4 mr-1.5" />
              Stop
            </Button>
          </div>
        )}

        {/* Input Area */}
        {!isRecording && (
          <div className="px-5 py-4 border-t bg-background">
            <div className="flex gap-2 items-end">
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                accept=".pdf,.docx,.doc,.jpeg,.jpg,.png"
                multiple
                className="hidden"
                data-testid="chat-file-input"
              />
              
              {/* Attachment Button */}
              <Button 
                type="button" 
                variant="ghost" 
                size="icon" 
                className="flex-shrink-0 h-10 w-10 rounded-full hover:bg-muted"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                data-testid="attach-file-btn"
              >
                {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Paperclip className="w-5 h-5" />}
              </Button>
              
              {/* Mention Button */}
              <Popover open={mentionOpen} onOpenChange={setMentionOpen}>
                <PopoverTrigger asChild>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="icon" 
                    className="flex-shrink-0 h-10 w-10 rounded-full hover:bg-muted"
                    data-testid="mention-btn"
                  >
                    <AtSign className="w-5 h-5" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Mention a user..." />
                    <CommandList>
                      <CommandEmpty>No users found</CommandEmpty>
                      <CommandGroup>
                        {allUsers.filter(u => u.id !== user?.id).map(u => (
                          <CommandItem key={u.id} onSelect={() => handleMention(u)} className="cursor-pointer">
                            <Avatar className="w-6 h-6 mr-2"><AvatarFallback className="text-xs">{getInitials(u.name)}</AvatarFallback></Avatar>
                            <span>{u.name}</span>
                            {selectedMentions.find(m => m.id === u.id) && <Check className="w-4 h-4 ml-auto text-primary" />}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
              
              {/* Voice Note Button */}
              <Button 
                type="button" 
                variant="ghost" 
                size="icon" 
                className="flex-shrink-0 h-10 w-10 rounded-full hover:bg-muted"
                onClick={startRecording}
                disabled={!!audioBlob}
                data-testid="voice-note-btn"
              >
                <Mic className="w-5 h-5" />
              </Button>
              
              {/* Message Input - Textarea for long messages */}
              <div className="flex-1 relative">
                <Textarea
                  ref={textareaRef}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type a message... (Ctrl+Enter to send)"
                  className="min-h-[44px] max-h-[150px] resize-none pr-12 rounded-2xl border-muted-foreground/20 focus:border-primary"
                  rows={1}
                  data-testid="chat-input"
                />
                <span className="absolute bottom-2 right-3 text-[10px] text-muted-foreground/50">
                  {newMessage.length > 0 && `${newMessage.length}`}
                </span>
              </div>
              
              {/* Send Button */}
              <Button 
                onClick={sendMessage}
                disabled={sending || (!newMessage.trim() && pendingAttachments.length === 0 && !audioBlob)}
                className="flex-shrink-0 h-10 w-10 rounded-full"
                size="icon"
                data-testid="send-message-btn"
              >
                {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
              </Button>
            </div>
            
            <p className="text-[10px] text-muted-foreground/60 mt-2 text-center">
              Press <kbd className="px-1 py-0.5 rounded bg-muted text-[9px]">Ctrl</kbd> + <kbd className="px-1 py-0.5 rounded bg-muted text-[9px]">Enter</kbd> to send
            </p>
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Message</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete this message? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={deleteMessage} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </DialogContent>
    </Dialog>
  );
};

export default ApplicationChat;
